import { __decorate, __metadata } from 'tslib';
import { DOCUMENT } from '@angular/common';
import * as i0 from '@angular/core';
import { Directive, afterNextRender, ChangeDetectorRef, ElementRef, NgZone, Component, ChangeDetectionStrategy, Inject, Input, ViewChild, HostBinding } from '@angular/core';
import { ngDocZoneOptimize } from '@ng-doc/ui-kit/observables';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { fromEvent, merge } from 'rxjs';
import { tap, map, pairwise, switchMap, takeUntil, take, filter, debounceTime } from 'rxjs/operators';
const _c0 = ["resizer"];
const _c1 = [[["", "ngDocPaneBack", ""]], [["", "ngDocPaneFront", ""]]];
const _c2 = ["[ngDocPaneBack]", "[ngDocPaneFront]"];
let NgDocPaneFrontDirective = /*#__PURE__*/(() => {
  class NgDocPaneFrontDirective {
    static {
      this.ɵfac = function NgDocPaneFrontDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgDocPaneFrontDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NgDocPaneFrontDirective,
        selectors: [["", "ngDocPaneFront", ""]]
      });
    }
  }
  return NgDocPaneFrontDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NgDocPaneBackDirective = /*#__PURE__*/(() => {
  class NgDocPaneBackDirective {
    static {
      this.ɵfac = function NgDocPaneBackDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgDocPaneBackDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NgDocPaneBackDirective,
        selectors: [["", "ngDocPaneBack", ""]]
      });
    }
  }
  return NgDocPaneBackDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NgDocPaneComponent = /*#__PURE__*/(() => {
  let NgDocPaneComponent = class NgDocPaneComponent {
    constructor(document, changeDetectorRef, elementRef, ngZone) {
      this.document = document;
      this.changeDetectorRef = changeDetectorRef;
      this.elementRef = elementRef;
      this.ngZone = ngZone;
      this.expanded = false;
      this.width = '0%';
      this.dragging = false;
      afterNextRender(() => {
        if (this.resizer) {
          const mouseDown$ = fromEvent(this.resizer.nativeElement, 'mousedown').pipe(tap(() => {
            this.dragging = true;
            this.changeDetectorRef.markForCheck();
          }));
          const mouseUp$ = fromEvent(this.document, 'mouseup').pipe(tap(() => {
            this.dragging = false;
            this.changeDetectorRef.markForCheck();
          }));
          const mouseMove$ = fromEvent(this.document, 'mousemove').pipe(map(event => event.clientX), pairwise(), map(([prev, next]) => next - prev));
          mouseDown$.pipe(switchMap(() => {
            const dragEvent$ = mouseMove$.pipe(takeUntil(mouseUp$));
            const clickEvent$ = mouseUp$.pipe(map(() => null), takeUntil(mouseMove$), take(1));
            return merge(dragEvent$, clickEvent$);
          }), filter(delta => delta !== 0), ngDocZoneOptimize(this.ngZone), untilDestroyed(this)).subscribe(delta => {
            delta === null ? this.toggle() : this.addDelta(delta);
          });
        }
        fromEvent(window, 'resize').pipe(debounceTime(100), untilDestroyed(this), ngDocZoneOptimize(this.ngZone)).subscribe(() => this.addDelta(0));
      });
      this.addDelta(0);
    }
    ngOnChanges({
      expanded
    }) {
      if (expanded) {
        expanded.currentValue ? this.addDelta(this.elementRef.nativeElement.offsetWidth) : this.addDelta(-this.elementRef.nativeElement.offsetWidth);
      }
    }
    toggle() {
      if (this.resizer) {
        const middle = this.elementRef.nativeElement.offsetWidth / 2;
        if (this.resizer.nativeElement.offsetLeft < middle) {
          this.addDelta(this.elementRef.nativeElement.offsetWidth);
        } else {
          this.addDelta(-this.elementRef.nativeElement.offsetWidth);
        }
      }
    }
    addDelta(delta) {
      if (this.resizer) {
        const maxWidth = this.elementRef.nativeElement.offsetWidth - this.resizer.nativeElement.offsetWidth;
        this.width = `${Math.min(maxWidth, Math.max(0, this.resizer.nativeElement.offsetLeft + delta))}px`;
        this.changeDetectorRef.detectChanges();
      }
    }
    static {
      this.ɵfac = function NgDocPaneComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgDocPaneComponent)(i0.ɵɵdirectiveInject(DOCUMENT), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.NgZone));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NgDocPaneComponent,
        selectors: [["ng-doc-pane"]],
        viewQuery: function NgDocPaneComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(_c0, 7);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.resizer = _t.first);
          }
        },
        hostVars: 1,
        hostBindings: function NgDocPaneComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵattribute("data-ng-doc-dragging", ctx.dragging);
          }
        },
        inputs: {
          expanded: "expanded"
        },
        features: [i0.ɵɵNgOnChangesFeature],
        ngContentSelectors: _c2,
        decls: 8,
        vars: 2,
        consts: [["resizer", ""], [1, "ng-doc-pane-back"], [1, "ng-doc-pane-content"], [1, "ng-doc-pane-resizer"], [1, "ng-doc-pane-front"]],
        template: function NgDocPaneComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef(_c1);
            i0.ɵɵelementStart(0, "div", 1)(1, "div", 2);
            i0.ɵɵprojection(2);
            i0.ɵɵelementEnd()();
            i0.ɵɵelement(3, "div", 3, 0);
            i0.ɵɵelementStart(5, "div", 4)(6, "div", 2);
            i0.ɵɵprojection(7, 1);
            i0.ɵɵelementEnd()();
          }
          if (rf & 2) {
            i0.ɵɵstyleProp("width", ctx.width);
          }
        },
        styles: ["[_nghost-%COMP%]{position:relative;display:flex;width:100%;border-radius:var(--ng-doc-pane-border-radius, var(--ng-doc-base-gutter));border:var(--ng-doc-pane-border, 1px solid var(--ng-doc-border-color));overflow:hidden}[data-ng-doc-dragging=false][_nghost-%COMP%]   .ng-doc-pane-back[_ngcontent-%COMP%]{transition:var(--ng-doc-transition) width}[_nghost-%COMP%]   .ng-doc-pane-front[_ngcontent-%COMP%]{position:relative;display:flex;width:100%;background-color:var(--ng-doc-pane-front-background, var(--ng-doc-pane-background, var(--ng-doc-base-1)));overflow:hidden;border-top-right-radius:var(--ng-doc-base-gutter);border-bottom-right-radius:var(--ng-doc-base-gutter);border:var(--ng-doc-pane-front-border, var(--ng-doc-pane-border))}[_nghost-%COMP%]   .ng-doc-pane-back[_ngcontent-%COMP%]{position:relative;display:flex;flex-shrink:0;width:100%;background-color:var(--ng-doc-pane-back-background, var(--ng-doc-pane-background, var(--ng-doc-base-1)));overflow:hidden;border-top-left-radius:var(--ng-doc-base-gutter);border-bottom-left-radius:var(--ng-doc-base-gutter);border:var(--ng-doc-pane-back-border, var(--ng-doc-pane-border))}[_nghost-%COMP%]   .ng-doc-pane-resizer[_ngcontent-%COMP%]{position:relative;flex-shrink:0;transform:translateZ(0);background-color:var(--ng-doc-base-3);width:16px;display:block;cursor:ew-resize}[_nghost-%COMP%]   .ng-doc-pane-resizer[_ngcontent-%COMP%]:after{content:\"\";display:flex;background:var(--ng-doc-base-10);border-radius:50px;position:absolute;width:4px;height:20%;top:50%;left:50%;transform:translateY(-50%) translate(-50%);opacity:1;transition:var(--ng-doc-transition) height}[_nghost-%COMP%]   .ng-doc-pane-content[_ngcontent-%COMP%]{display:flex;align-items:center;justify-content:center;flex-shrink:0;width:100%;height:100%;min-width:var(--ng-doc-pane-content-min-width, 200px)}"],
        changeDetection: 0
      });
    }
  };
  NgDocPaneComponent = __decorate([UntilDestroy(), __metadata("design:paramtypes", [Document, ChangeDetectorRef, ElementRef, NgZone])], NgDocPaneComponent);
  return NgDocPaneComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NgDocPaneBackDirective, NgDocPaneComponent, NgDocPaneFrontDirective };
